import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Input } from 'antd';
import type { InputRef } from 'antd';

import MyModal from 'components/MyModal';
import Spin from 'components/Spin';
import { tap } from 'rxjs/operators';
import type { Group } from 'v2/interfaces/access-control';
import accessControlService from 'v2/services/access-control';

import FormGroupModule from './Module';

type Props = {
  current?: Group;
  onComplete: () => void;
  onCancel: () => void;
};

export default function FormGroup({ onCancel, onComplete, current }: Props) {
  const [isLoadingModules] = useObservable(() => accessControlService.isLoadingModules$, []);
  const [modules] = useObservable(() => accessControlService.getModules(), []);
  const [saveGroup] = useCallbackObservable(
    data =>
      accessControlService.saveGroup(data).pipe(
        tap(
          () => {
            toast.success('Sucesso, grupo criado!');
            onComplete();
            setIsSaving(false);
          },
          err => {
            toast.error(err);
            setIsSaving(false);
          }
        )
      ),
    []
  );
  const [updateGroup] = useCallbackObservable(
    data =>
      accessControlService.updateGroup(data).pipe(
        tap(
          () => {
            toast.success('Sucesso, grupo atualizado!');
            onComplete();
            setIsSaving(false);
          },
          err => {
            toast.error(err);
            setIsSaving(false);
          }
        )
      ),
    []
  );
  const [groupName, setGroupName] = useState(current?.name || '');
  const [selectedActionsId, setSelectedActionsId] = useState(() => {
    if (!current) return [];
    const actionsData = Object.values(current.privileges).flatMap(privilege => privilege.actions);
    return actionsData.map(action => action.id);
  });
  const [isSaving, setIsSaving] = useState(false);

  const groupNameInputRef = useRef<InputRef>(null);

  const isEditing = !!current;

  function handleModalSubmit() {
    setIsSaving(true);

    if (!groupName) {
      groupNameInputRef.current.focus();
      toast.error('O nome do grupo é obrigatório.');
      setIsSaving(false);
      return;
    }

    if (!selectedActionsId.length) {
      toast.error('O grupo precisa ter pelo menos uma ação.');
      setIsSaving(false);
      return;
    }

    if (isEditing) {
      updateGroup({
        id: current.id,
        name: groupName,
        actions: selectedActionsId
      });
      return;
    }

    saveGroup({
      name: groupName,
      actions: selectedActionsId
    });
  }

  const handleActionsChange = useCallback((id: number) => {
    setSelectedActionsId(old => {
      if (old.includes(id)) {
        return old.filter(actionId => actionId !== id);
      }

      return [...old, id];
    });
  }, []);

  return (
    <MyModal
      hasActive
      title='Grupo de ações'
      subTitle='Adicione os módulos e as ações de cada módulo para criar um grupo.'
      closeModal={onCancel}
      textButtonOk={isEditing ? 'Atualizar' : 'Adicionar'}
      hasButtons={true}
      handleClickOk={handleModalSubmit}
      loading={isSaving || isLoadingModules}
    >
      {isLoadingModules && <Spin />}

      {!isLoadingModules && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label className='mb-1 block text-sm text-gray-600'>Nome do grupo*</label>
            <Input
              ref={groupNameInputRef}
              value={groupName}
              disabled={isSaving}
              onChange={e => setGroupName(e.target.value)}
              placeholder='Nome do grupo'
            />
          </div>

          {!!modules?.length &&
            modules.map((module, index) => (
              <FormGroupModule
                key={module.id}
                handleActionsChange={handleActionsChange}
                selectedActionsId={selectedActionsId}
                module={module}
                initialOpen={index === 0}
              />
            ))}
        </>
      )}
    </MyModal>
  );
}
